import { ENV_CONFIG } from 'config/environment';
import { dollarsToCents } from 'helpers/utils';
import {
  AdminCartPage,
  AdminShoppingCart,
  AdminShoppingCartCreatePayload,
  CartProcessingRedirect,
  CartProcessingRedirectWritable,
  RecipientSelectionMode,
  ShoppingCartRecipientStatus,
} from 'store/shoppingCart/types';
import { DEFAULT_DONATION_AMOUNTS, DEFAULT_MAX_RESULTS } from './constants';
import { ShoppingCartFormValues } from './types';

const isWritableProcessor = (
  value: CartProcessingRedirect | null
): value is CartProcessingRedirectWritable =>
  value === CartProcessingRedirect.STRIPE || value === CartProcessingRedirect.DE_OATH;

const getImagePreviewUrl = (page: AdminCartPage) => {
  const filename = page.image_filenames?.principal_logo;
  if (!page.has_image || !filename) return undefined;
  return `${ENV_CONFIG().CLOUD_STORAGE.URL}/${ENV_CONFIG().CLOUD_STORAGE.TAGS}/${filename}`;
};

export const getActivateInitialValues = (
  suggestedQueryString: string | null | undefined
): ShoppingCartFormValues => ({
  recipient_selection_mode: RecipientSelectionMode.RECIPIENTS_LIST,
  processing_redirect: CartProcessingRedirect.STRIPE,
  impact_score_visibility: true,
  show_more_visibility: true,
  query_string: suggestedQueryString ?? undefined,
  donation_amounts: DEFAULT_DONATION_AMOUNTS,
  max_results: DEFAULT_MAX_RESULTS,
});

export const getEditInitialValues = (cart: AdminShoppingCart): ShoppingCartFormValues => {
  const { page } = cart;
  return {
    recipient_selection_mode: cart.recipient_selection_mode,
    // A drifted page can hold a processor this form can't write back, so leave it unset.
    processing_redirect: isWritableProcessor(page.processing_redirect)
      ? page.processing_redirect
      : undefined,
    impact_score_visibility: page.impact_score_visibility,
    show_more_visibility: page.show_more_visibility,
    recipient_order_by: page.recipient_order_by ?? '',
    query_string: page.query_string,
    page_name: page.page_name,
    page_desc: page.page_desc,
    page_background: page.page_background,
    page_color_text: page.page_color_text,
    preview: getImagePreviewUrl(page),
    max_results: cart.effective_max_results,
    criteria: cart.criteria,
    visible_type_filters: cart.visible_type_filters,
    recipients_selected: cart.recipients
      .filter(
        recipient => recipient.shopping_cart_recipient_status === ShoppingCartRecipientStatus.ACTIVE
      )
      .map(recipient => ({ value: recipient.recipient_id, label: recipient.recipient_name })),
  };
};

const buildSharedFields = (values: ShoppingCartFormValues) => ({
  recipient_selection_mode: values.recipient_selection_mode,
  query_string: values.query_string,
  page_name: values.page_name,
  page_desc: values.page_desc,
  processing_redirect: values.processing_redirect,
  max_results: values.max_results,
  impact_score_visibility: values.impact_score_visibility,
  show_more_visibility: values.show_more_visibility,
  recipient_order_by: values.recipient_order_by || null,
  page_background: values.page_background,
  page_color_text: values.page_color_text,
  visible_type_filters: values.visible_type_filters?.length ? values.visible_type_filters : null,
  ...(values.recipient_selection_mode === RecipientSelectionMode.CRITERIA_MATCH
    ? { criteria: values.criteria }
    : { recipients: values.recipients_selected?.map(item => item.value) }),
});

export const buildActivatePayload = (
  values: ShoppingCartFormValues
): AdminShoppingCartCreatePayload => ({
  ...buildSharedFields(values),
  donation_amounts: (values.donation_amounts ?? []).map(dollarsToCents),
});

// has_image is only cleared here; setting it waits for a successful upload.
export const buildUpdatePayload = (
  values: ShoppingCartFormValues
): Partial<AdminShoppingCartCreatePayload> => ({
  ...buildSharedFields(values),
  ...(!values.preview && { has_image: false }),
});
